import { useTranslation } from 'react-i18next';
import type { TenantMember } from '@/api/tenants/tenantsApi.types';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { useTenantMembersQuery } from '@/hooks/useTenantMembersQuery';
import { buildNameInitials } from '@/lib/nameInitials';

interface MembersTabProps {
  tenantId: string;
}

export function MembersTab({ tenantId }: MembersTabProps) {
  const { t } = useTranslation('modules');
  const membersQuery = useTenantMembersQuery(tenantId);

  if (membersQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (membersQuery.isError === true || membersQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          membersQuery.refetch();
        }}
      />
    );
  }

  const members = membersQuery.data;

  return (
    <Card padding="md">
      <h2 className="text-sm font-bold text-zooindigo mb-1">{t('admin.membersTitle')}</h2>
      <p className="text-xs text-zoomuted mb-4">{t('admin.membersSubtitle')}</p>

      {members.length === 0 ? (
        <p className="text-sm text-zoomuted py-2">{t('admin.membersEmpty')}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {members.map(function renderMember(member) {
            return <MemberRow key={member.userId} member={member} />;
          })}
        </div>
      )}
    </Card>
  );
}

function MemberRow({ member }: { member: TenantMember }) {
  const { t } = useTranslation('modules');
  const roleLabel = t('admin.roleName.' + member.roleCode, { defaultValue: member.roleCode });

  return (
    <div className="flex items-center gap-3 p-3 border border-zooborder rounded-xl">
      <span className="w-10 h-10 rounded-full bg-zoovpalelavender text-zooprimary flex items-center justify-center font-bold text-sm shrink-0">
        {buildNameInitials(member.fullName)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-zooindigo truncate">{member.fullName}</div>
        <div className="text-xs text-zoomuted truncate">{member.email}</div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {member.isOwner === true ? (
          <Badge tone="accent" size="sm">
            {t('admin.ownerBadge')}
          </Badge>
        ) : null}
        <Badge tone="muted" size="sm">
          {roleLabel}
        </Badge>
      </div>
    </div>
  );
}
